/**
 * Maritime / vetting glossary.
 *
 * Each entry is keyed by an UPPERCASE shorthand (matched case-insensitively
 * by `<GlossaryTerm term="ISM" />`). Keep tl;dr to one sentence, written
 * for an inspector who already knows the basics but might forget which is which.
 */

export interface GlossaryEntry {
  /** The acronym or term as displayed. */
  term: string;
  /** Full expansion (e.g. "International Safety Management Code"). */
  expansion: string;
  /** Single-sentence summary for the hover popover. */
  tldr: string;
  /** Optional link to a full article slug for "Read more". */
  relatedSlug?: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  CDI: {
    term: 'CDI',
    expansion: 'Chemical Distribution Institute',
    tldr: 'Industry body that runs the CDI Marine inspection scheme for chemical and gas tankers; member companies share inspection reports.',
    relatedSlug: 'standards-vetting/cdi-vs-sire',
  },
  SIRE: {
    term: 'SIRE',
    expansion: 'Ship Inspection Report Programme',
    tldr: 'OCIMF\'s tanker inspection scheme; SIRE 2.0 is the current digital, observation-based version.',
    relatedSlug: 'standards-vetting/cdi-vs-sire',
  },
  OCIMF: {
    term: 'OCIMF',
    expansion: 'Oil Companies International Marine Forum',
    tldr: 'The trade association that publishes SIRE, MEG, ISGOTT and most oil-tanker safety guidance.',
    relatedSlug: 'standards-vetting/cdi-vs-sire',
  },
  'OCIMF SIRE 2.0': {
    term: 'SIRE 2.0',
    expansion: 'OCIMF Ship Inspection Report Programme, version 2.0',
    tldr: 'OCIMF\'s 2024+ digital inspection regime: tablet-based, three-pillar (process, hardware, human factors), with mandatory photographic evidence.',
    relatedSlug: 'standards-vetting/cdi-vs-sire',
  },
  CEFIC: {
    term: 'CEFIC',
    expansion: 'European Chemical Industry Council',
    tldr: 'Co-founded CDI; represents European chemical producers and shippers.',
  },
  IMO: {
    term: 'IMO',
    expansion: 'International Maritime Organization',
    tldr: 'UN agency that issues SOLAS, MARPOL, STCW and the other maritime conventions you cite in observations.',
    relatedSlug: 'standards-imo/solas-key-chapters',
  },
  SOLAS: {
    term: 'SOLAS',
    expansion: 'Safety Of Life At Sea',
    tldr: 'IMO\'s safety bible — covers construction, fire, life-saving, navigation, carriage of cargoes and security.',
    relatedSlug: 'standards-imo/solas-key-chapters',
  },
  MARPOL: {
    term: 'MARPOL',
    expansion: 'International Convention for the Prevention of Pollution from Ships',
    tldr: 'IMO\'s pollution-prevention convention; six annexes covering oil, chemicals, sewage, garbage, packaged HNS and air emissions.',
    relatedSlug: 'standards-imo/marpol-annexes',
  },
  ISM: {
    term: 'ISM',
    expansion: 'International Safety Management Code',
    tldr: 'SOLAS Chapter IX — the company\'s safety management system; you verify the DOC, SMC, master\'s review and non-conformity records.',
    relatedSlug: 'standards-imo/ism-code',
  },
  ISPS: {
    term: 'ISPS',
    expansion: 'International Ship and Port Facility Security Code',
    tldr: 'SOLAS Chapter XI-2 — security plans, SSAS, security levels, drills and the ISSC certificate.',
    relatedSlug: 'standards-imo/isps-code',
  },
  MLC: {
    term: 'MLC',
    expansion: 'Maritime Labour Convention, 2006',
    tldr: 'ILO "fourth pillar" — seafarer rights, hours of rest, accommodation, food, medical care; the DMLC and MLC certificate document compliance.',
    relatedSlug: 'standards-imo/mlc-2006',
  },
  STCW: {
    term: 'STCW',
    expansion: 'Standards of Training, Certification and Watchkeeping for Seafarers',
    tldr: 'IMO convention on certification, watchkeeping and rest hours; you verify CoCs, endorsements and rest-hour records.',
    relatedSlug: 'standards-imo/stcw',
  },
  ISGOTT: {
    term: 'ISGOTT',
    expansion: 'International Safety Guide for Oil Tankers and Terminals',
    tldr: 'ICS/OCIMF/IAPH industry guide to tanker and terminal safety — ship/shore checklist, gas-freeing, mooring, hot work, STS.',
    relatedSlug: 'standards-imo/isgott',
  },
  COSWP: {
    term: 'COSWP',
    expansion: 'Code of Safe Working Practices for Merchant Seafarers',
    tldr: 'UK MCA\'s practical "how-to-not-get-killed-on-deck" handbook; widely cited even on non-UK flag vessels.',
  },
  ILO: {
    term: 'ILO',
    expansion: 'International Labour Organization',
    tldr: 'UN agency for labour standards; ILO Convention 152 covers occupational safety and health for dock work.',
  },
  IBC: {
    term: 'IBC',
    expansion: 'International Bulk Chemical Code',
    tldr: 'Mandatory IMO code for chemical tankers — ship type, tank arrangements, materials, cargo containment, list of products in Ch. 17.',
  },
  IGC: {
    term: 'IGC',
    expansion: 'International Gas Carrier Code',
    tldr: 'Mandatory IMO code for liquefied-gas carriers built after 1986 — design, materials, cargo containment, list of products in Ch. 19.',
  },
  IAPP: {
    term: 'IAPP',
    expansion: 'International Air Pollution Prevention Certificate',
    tldr: 'MARPOL Annex VI certificate showing the engine and ship comply with NOx, SOx and ODS rules.',
  },
  IOPP: {
    term: 'IOPP',
    expansion: 'International Oil Pollution Prevention Certificate',
    tldr: 'MARPOL Annex I certificate, with the IOPP supplement listing tank arrangements, OWS, ODMCS, sludge tank capacity etc.',
  },
  'IPP NLS': {
    term: 'NLS Certificate',
    expansion: 'International Pollution Prevention Certificate for the Carriage of Noxious Liquid Substances in Bulk',
    tldr: 'MARPOL Annex II certificate for chemical tankers carrying NLS cargoes; the supplement (P&A Manual) lists products and stripping arrangements.',
  },
  IEEC: {
    term: 'IEEC',
    expansion: 'International Energy Efficiency Certificate',
    tldr: 'MARPOL Annex VI Ch. 4 certificate showing EEDI/EEXI compliance; the SEEMP is its companion document.',
  },
  ISSC: {
    term: 'ISSC',
    expansion: 'International Ship Security Certificate',
    tldr: 'ISPS certificate confirming the SSP has been approved and verified by an RSO.',
  },
  MSC: {
    term: 'MSC',
    expansion: 'Maritime Safety Committee',
    tldr: 'IMO\'s top safety committee — issues MSC circulars and resolutions you\'ll cite alongside SOLAS.',
  },
  MEPC: {
    term: 'MEPC',
    expansion: 'Marine Environment Protection Committee',
    tldr: 'IMO\'s environment committee — issues MEPC circulars and resolutions, including CII / EEXI / SEEMP guidance.',
  },
  IMSBC: {
    term: 'IMSBC',
    expansion: 'International Maritime Solid Bulk Cargoes Code',
    tldr: 'Mandatory code for solid bulk cargoes — Group A (liquefaction), B (chemical), C (neither); cargo declaration and TML required.',
  },
  IMDG: {
    term: 'IMDG',
    expansion: 'International Maritime Dangerous Goods Code',
    tldr: 'Mandatory code for packaged dangerous goods — classification, packing, marking, segregation, DGD and stowage planning.',
  },
  FSS: {
    term: 'FSS',
    expansion: 'Fire Safety Systems Code',
    tldr: 'Detailed technical standards behind SOLAS Ch. II-2 — extinguishers, fixed gas, foam, sprinkler, structure of fire pumps, etc.',
  },
  GMDSS: {
    term: 'GMDSS',
    expansion: 'Global Maritime Distress and Safety System',
    tldr: 'SOLAS Ch. IV radiocommunications setup — DSC, NAVTEX, EPIRB, SART, Inmarsat / Iridium; check the radio log and battery tests.',
  },
  ECDIS: {
    term: 'ECDIS',
    expansion: 'Electronic Chart Display and Information System',
    tldr: 'SOLAS-mandated electronic chart system; verify ENC currency, alarms, route plan and back-up arrangement.',
  },
  AIS: {
    term: 'AIS',
    expansion: 'Automatic Identification System',
    tldr: 'VHF transponder broadcasting position, identity and voyage data; the foundation for vessel tracking.',
  },
  BNWAS: {
    term: 'BNWAS',
    expansion: 'Bridge Navigational Watch Alarm System',
    tldr: 'SOLAS Ch. V dead-man alarm on the bridge; verify operation, settings and that it isn\'t bypassed.',
  },
  ARPA: {
    term: 'ARPA',
    expansion: 'Automatic Radar Plotting Aid',
    tldr: 'Radar feature that auto-tracks targets and computes CPA/TCPA; required on most SOLAS ships.',
  },
  COLREGS: {
    term: 'COLREGS',
    expansion: 'Convention on the International Regulations for Preventing Collisions at Sea',
    tldr: 'The "Rules of the Road" — verify navigation lights, shapes, sound signals and watchkeeping.',
  },
  EEDI: {
    term: 'EEDI',
    expansion: 'Energy Efficiency Design Index',
    tldr: 'Design-stage CO2 metric for new ships under MARPOL Annex VI — recorded on the IEEC.',
  },
  EEXI: {
    term: 'EEXI',
    expansion: 'Energy Efficiency Existing Ship Index',
    tldr: 'In-service one-off CO2 efficiency requirement for existing ships ≥400 GT; technical file kept on board.',
  },
  CII: {
    term: 'CII',
    expansion: 'Carbon Intensity Indicator',
    tldr: 'Operational annual CO2 rating (A-E) for ships ≥5000 GT; D-rating for 3 yrs or E-rating for 1 yr triggers a corrective action plan in the SEEMP.',
  },
  SEEMP: {
    term: 'SEEMP',
    expansion: 'Ship Energy Efficiency Management Plan',
    tldr: 'Mandatory ship-specific plan covering EEXI, CII and operational efficiency measures; Part III holds the corrective action plan.',
  },
  OWS: {
    term: 'OWS',
    expansion: 'Oily Water Separator',
    tldr: 'MARPOL Annex I 15-ppm bilge water filter; verify ODMCS readouts, magic-pipe sealing and ORB Part I entries.',
  },
  ODMCS: {
    term: 'ODMCS',
    expansion: 'Oil Discharge Monitoring and Control System',
    tldr: 'MARPOL Annex I cargo-side discharge monitor on tankers; verify calibration record and overboard valve seals.',
  },
  IGS: {
    term: 'IGS',
    expansion: 'Inert Gas System',
    tldr: 'SOLAS Ch. II-2 system that keeps cargo-tank atmosphere below 8% O2; check P/V breaker, deck water seal, oxygen analyser cal.',
  },
  BCH: {
    term: 'BCH',
    expansion: 'Bulk Chemical Code',
    tldr: 'Pre-IBC code still applicable to chemical tankers built before 1 July 1986.',
  },
  NLS: {
    term: 'NLS',
    expansion: 'Noxious Liquid Substance',
    tldr: 'MARPOL Annex II liquid cargo — categorised X / Y / Z / OS based on environmental hazard.',
  },
  HNS: {
    term: 'HNS',
    expansion: 'Hazardous and Noxious Substances',
    tldr: 'Cargoes covered by MARPOL Annex III (packaged form) and the HNS Convention 2010 (liability).',
  },
  MFAG: {
    term: 'MFAG',
    expansion: 'Medical First Aid Guide for Use in Accidents Involving Dangerous Goods',
    tldr: 'IMO/WHO/ILO guide carried alongside the IMDG Code on ships carrying dangerous goods.',
  },
  IMSAR: {
    term: 'IAMSAR',
    expansion: 'International Aeronautical and Maritime Search and Rescue Manual',
    tldr: 'Three-volume IMO/ICAO manual — Vol III is on board ships, used during distress.',
  },
  IMPA: {
    term: 'IMPA',
    expansion: 'International Marine Purchasing Association',
    tldr: 'Best known to inspectors for the IMPA pilot ladder safety poster — the simple "right vs wrong" reference.',
  },
  NI: {
    term: 'NI',
    expansion: 'Nautical Institute',
    tldr: 'Professional body publishing the Mariner\'s Role in Collecting Evidence, mooring, pilotage and many bridge-team books.',
  },
  ICS: {
    term: 'ICS',
    expansion: 'International Chamber of Shipping',
    tldr: 'Industry body that co-publishes ISGOTT, the Bridge Procedures Guide and ECDIS guidance.',
  },
  'P&I': {
    term: 'P&I',
    expansion: 'Protection and Indemnity Club',
    tldr: 'Mutual insurance club covering third-party liabilities; their loss-prevention bulletins are quotable for observation guidance.',
  },
  LR: {
    term: 'LR',
    expansion: 'Lloyd\'s Register',
    tldr: 'Classification society; an IACS member.',
  },
  DNV: {
    term: 'DNV',
    expansion: 'Det Norske Veritas',
    tldr: 'Classification society; an IACS member (formerly DNV GL).',
  },
  BV: {
    term: 'BV',
    expansion: 'Bureau Veritas',
    tldr: 'Classification society; an IACS member.',
  },
  ABS: {
    term: 'ABS',
    expansion: 'American Bureau of Shipping',
    tldr: 'Classification society; an IACS member.',
  },
  KR: {
    term: 'KR',
    expansion: 'Korean Register',
    tldr: 'Classification society; an IACS member.',
  },
  NK: {
    term: 'NK / ClassNK',
    expansion: 'Nippon Kaiji Kyokai',
    tldr: 'Japanese classification society; an IACS member.',
  },
  RINA: {
    term: 'RINA',
    expansion: 'Registro Italiano Navale',
    tldr: 'Italian classification society; an IACS member.',
  },
  CCS: {
    term: 'CCS',
    expansion: 'China Classification Society',
    tldr: 'Chinese classification society; an IACS member.',
  },
  IACS: {
    term: 'IACS',
    expansion: 'International Association of Classification Societies',
    tldr: 'Federation of the major class societies; publishes Unified Requirements (URs) and Procedural Requirements (PRs).',
  },
  RO: {
    term: 'RO',
    expansion: 'Recognized Organization',
    tldr: 'Class society or other body delegated by a flag State to issue statutory certificates on its behalf.',
  },
  SMC: {
    term: 'SMC',
    expansion: 'Safety Management Certificate',
    tldr: 'ISM ship-side certificate; valid 5 yrs with intermediate (between 2nd and 3rd anniversary) audit.',
  },
  DOC: {
    term: 'DOC',
    expansion: 'Document of Compliance',
    tldr: 'ISM company-side certificate; an annual audit is required on board to keep it valid.',
  },
  CSR: {
    term: 'CSR',
    expansion: 'Continuous Synopsis Record',
    tldr: 'SOLAS Ch. XI-1 record of ownership, flag, class and ISM/ISPS history; full set of records, in date order, must be on board.',
  },
  PSC: {
    term: 'PSC',
    expansion: 'Port State Control',
    tldr: 'Inspection of foreign ships in national ports; deficiencies and detentions are public on each MoU\'s database.',
  },
  MoU: {
    term: 'MoU',
    expansion: 'Memorandum of Understanding',
    tldr: 'Regional PSC agreement — Paris MoU, Tokyo MoU, USCG, Black Sea MoU etc.',
  },
  'Paris MoU': {
    term: 'Paris MoU',
    expansion: 'Paris Memorandum of Understanding on Port State Control',
    tldr: 'Largest PSC region (27 European + Canadian authorities); publishes the white/grey/black list of flags and the THETIS database.',
  },
  'Tokyo MoU': {
    term: 'Tokyo MoU',
    expansion: 'Tokyo Memorandum of Understanding on Port State Control',
    tldr: 'Asia-Pacific PSC region; publishes the APCIS database.',
  },
  USCG: {
    term: 'USCG',
    expansion: 'United States Coast Guard',
    tldr: 'US PSC authority — runs its own targeting matrix and publishes Port State Control annual reports.',
  },
  FOC: {
    term: 'FOC / FoC',
    expansion: 'Flag of Convenience',
    tldr: 'Open registry where the beneficial owner is in a different country; the ITF maintains a list.',
  },
  LSA: {
    term: 'LSA Code',
    expansion: 'International Life-Saving Appliance Code',
    tldr: 'Mandatory technical code behind SOLAS Ch. III lifeboats, liferafts, davits, immersion suits, EPIRB, SART, etc.',
  },
  ESP: {
    term: 'ESP Code',
    expansion: 'Enhanced Survey Programme Code',
    tldr: 'Mandatory enhanced survey requirements for bulk carriers and oil tankers; CAPs / CAR / thickness measurements live in the ESP file.',
  },
  MEG: {
    term: 'MEG',
    expansion: 'Mooring Equipment Guidelines',
    tldr: 'OCIMF\'s definitive guide on mooring; MEG4 introduced LDBF, line management plans, MBL/SWL conventions.',
  },
  MBL: {
    term: 'MBL',
    expansion: 'Minimum Breaking Load',
    tldr: 'The lowest load at which a new mooring line is certified to break; LDBF and tail MBL are derived from it.',
  },
  LDBF: {
    term: 'LDBF',
    expansion: 'Line Design Break Force',
    tldr: 'MEG4 successor to MBL — the design strength used to size winches and tails on new builds.',
  },
  LOF: {
    term: 'LOF',
    expansion: 'Lloyd\'s Open Form',
    tldr: 'Standard salvage contract on a "no cure, no pay" basis; carried on board for emergency use.',
  },
  SSP: {
    term: 'SSP',
    expansion: 'Ship Security Plan',
    tldr: 'ISPS plan approved by the flag (or RO); not openly inspectable beyond the sections explicitly allowed by ISPS Part A.',
    relatedSlug: 'standards-imo/isps-code',
  },
  SSAS: {
    term: 'SSAS',
    expansion: 'Ship Security Alert System',
    tldr: 'Covert alarm transmitting to the company / flag; tested per the SSP — never tested without prior coordination.',
  },
  CSO: {
    term: 'CSO',
    expansion: 'Company Security Officer',
    tldr: 'ISPS shore-side officer responsible for the SSP and security audits.',
  },
  SSO: {
    term: 'SSO',
    expansion: 'Ship Security Officer',
    tldr: 'ISPS officer on board (often the master or chief officer) — runs drills and maintains the security records.',
  },
  DPA: {
    term: 'DPA',
    expansion: 'Designated Person Ashore',
    tldr: 'ISM shore contact with direct access to the highest level of management; verified by name, contact details and master\'s 24/7 access.',
  },
  CoC: {
    term: 'CoC',
    expansion: 'Certificate of Competency',
    tldr: 'STCW seafarer certification — verify number, capacity, expiry, endorsements and dangerous-cargo / GMDSS / ECDIS specifics.',
  },
  HVPQ: {
    term: 'HVPQ',
    expansion: 'Harmonised Vessel Particulars Questionnaire',
    tldr: 'OCIMF questionnaire describing the tanker; SIRE 2.0 inspectors use it as the cross-check for everything they see on board.',
  },
  SID: {
    term: 'SID',
    expansion: 'Ship Inspection Database',
    tldr: 'CDI\'s online database of inspection reports; member companies share access.',
  },
  PSCO: {
    term: 'PSCO',
    expansion: 'Port State Control Officer',
    tldr: 'The flag-State or PSC officer who inspects foreign ships in port; you may meet them during your CDI/SIRE visit.',
  },
  RSO: {
    term: 'RSO',
    expansion: 'Recognized Security Organization',
    tldr: 'ISPS-approved body (often a class society) authorised to verify SSPs and issue ISSCs.',
  },
  ECA: {
    term: 'ECA',
    expansion: 'Emission Control Area',
    tldr: 'Designated MARPOL Annex VI area with stricter SOx (0.10%) and/or NOx Tier III rules — N. America, US Caribbean, North Sea, Baltic, Med (from 2025).',
  },
  ORB: {
    term: 'ORB',
    expansion: 'Oil Record Book',
    tldr: 'MARPOL Annex I record book — Part I (machinery) on every ship ≥400 GT, Part II (cargo) on tankers ≥150 GT; sample at least 12 months.',
  },
  GRB: {
    term: 'GRB',
    expansion: 'Garbage Record Book',
    tldr: 'MARPOL Annex V record of garbage discharges and incinerations; required on ships ≥100 GT and all carrying ≥15 persons.',
  },
  CRB: {
    term: 'CRB',
    expansion: 'Cargo Record Book',
    tldr: 'MARPOL Annex II record book on chemical tankers — every transfer, ballasting, tank washing and discharge must be entered.',
  },
  STS: {
    term: 'STS',
    expansion: 'Ship-to-Ship Transfer',
    tldr: 'Cargo transfer between two ships; MARPOL Annex I Ch. 8 STS plan and ISGOTT Ch. 7 procedures apply.',
  },
  CAP: {
    term: 'CAP',
    expansion: 'Condition Assessment Programme',
    tldr: 'Voluntary class survey rating a ship\'s condition (CAP 1-4); often a SIRE/CDI screening criterion for older tankers.',
  },
  MSDS: {
    term: 'SDS / MSDS',
    expansion: 'Safety Data Sheet (formerly MSDS)',
    tldr: 'Cargo-specific safety data; required by SOLAS VI/5-1 before loading. Should be in English and on board for every cargo.',
  },
  FAL: {
    term: 'FAL',
    expansion: 'Convention on Facilitation of International Maritime Traffic',
    tldr: 'IMO convention standardising port-arrival forms; from 2024 a Maritime Single Window is mandatory.',
  },
  MASTREP: {
    term: 'MASTREP',
    expansion: 'Master Report',
    tldr: 'Voluntary mandatory ship-reporting scheme — each region has its own (e.g. AUSREP, JASREP, USCG AMVER).',
  },
  AMVER: {
    term: 'AMVER',
    expansion: 'Automated Mutual-Assistance Vessel Rescue System',
    tldr: 'USCG voluntary global ship-reporting system used in SAR; many vetting templates check for participation.',
  },
};

/** Look up an entry case-insensitively. */
export function lookupGlossary(term: string): GlossaryEntry | undefined {
  const upper = term.toUpperCase();
  if (GLOSSARY[upper]) return GLOSSARY[upper];
  // also try original case (for entries like "OCIMF SIRE 2.0")
  if (GLOSSARY[term]) return GLOSSARY[term];
  // last resort: find first key that matches by ignoring case
  const key = Object.keys(GLOSSARY).find((k) => k.toUpperCase() === upper);
  return key ? GLOSSARY[key] : undefined;
}

export const GLOSSARY_TERMS: GlossaryEntry[] = Object.values(GLOSSARY).sort((a, b) =>
  a.term.localeCompare(b.term),
);
